import React, { useContext } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Video as VideoIcon } from "lucide-react";
import { Note } from "protogen/notes_pb";
import { Suggestions } from "protogen/suggestions_pb";
import { VideoRoom, VideoRoom_State } from "protogen/video_pb";
import { useRunWorkflow } from "services/workflow";
import { SuggestionsList } from "components/activity/Suggestions";
import SuggestionActions, {
  SuggestionActionsHandle,
} from "components/activity/SuggestionActions";
import { RunWorkflowRequest } from "protogen/workflow_service_pb";
import { SuggestionEntityType } from "protogen/suggestions_pb";
import Spinner from "components/common/Spinner";
import NotesEditor from "./NotesEditor";
import VideoCallSetup from "components/video/VideoCallSetup";
import VideoCallContainer from "components/video/VideoCallContainer";
import { LocalUserChoices } from "@livekit/components-react";
import { VideoCallContext } from "components/context/VideoCallContext";
import useIsMobile from "components/hooks/useIsMobile";

type SuggestionsSectionProps = {
  videoRoom: VideoRoom;
  suggestions: Suggestions;
  familyRef: string;
  title: string;
  onRefreshSuggestions: () => void;
};

const Loader = () => (
  <Box
    display="flex"
    flexDirection="row"
    gap="10px"
    alignItems="center"
    sx={{
      width: "100%",
      border: "1px solid #D4D4D4",
      borderRadius: "12px",
      padding: "16px 12px",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
    }}
  >
    <Spinner size={20} />
    <Typography color="#198282" variant="body" fontWeight={600}>
      Analyzing call
    </Typography>
  </Box>
);

const SuggestionsSection = ({
  videoRoom,
  suggestions,
  familyRef,
  onRefreshSuggestions,
}: SuggestionsSectionProps) => {
  const actionsRef = React.useRef<SuggestionActionsHandle | null>(null);
  const { request: workflowRequest, loading } = useRunWorkflow();
  const isFreshlyCompleteWithoutSuggestions =
    videoRoom.state === VideoRoom_State.COMPLETED &&
    // Updated within the last 30 seconds
    Date.now() - Number(videoRoom.lastUpdatedSec) * 1000 < 30000 &&
    !suggestions.hasSuggestions;
  const handleClick = async () => {
    await workflowRequest(
      new RunWorkflowRequest({
        workflowName: "process-video-call",
        arguments: [],
        entityType: SuggestionEntityType.VIDEOCALL,
        entityRef: videoRoom.uuid,
      }),
    );
    onRefreshSuggestions();
  };
  return (
    <>
      {videoRoom.state === VideoRoom_State.COMPLETED && (
        <Typography variant="bodySmall">
          Summary and task suggestions will be generated at the end of the call.{" "}
          <span
            style={{
              color: "#198282",
              fontWeight: "600",
              cursor: "pointer",
            }}
            onClick={handleClick}
          >
            Try now?
          </span>
        </Typography>
      )}
      <Box
        display="flex"
        flexDirection="column"
        gap="24px"
        mb="16px"
        width="100%"
      >
        {(loading || isFreshlyCompleteWithoutSuggestions) && <Loader />}
        {!loading && suggestions?.hasSuggestions && (
          <SuggestionsList
            inlineMode={true}
            suggestions={suggestions}
            actions={{
              suggestTask: (t) => actionsRef.current?.suggestTask(t),
              suggestFact: (f) => actionsRef.current?.suggestFact(f),
              suggestEvent: (e) => actionsRef.current?.suggestEvent(e),
            }}
            isOpen={true}
            entityRef={videoRoom.ref}
            entityType={"videocall"}
            familyRef={familyRef}
          />
        )}
      </Box>
      <SuggestionActions ref={actionsRef} familyRef={familyRef} />
    </>
  );
};

type Props = {
  note: Note | null;
  isCallActive: boolean;
  onReJoin: () => void;
  videoRoom: VideoRoom | null;
  suggestions: Suggestions | null;
  familyRef: string;
  onRefreshSuggestions: () => void;
  onVideoSetupJoin?: (choices: LocalUserChoices) => void;
};

export default ({
  note,
  isCallActive,
  onReJoin,
  videoRoom,
  suggestions,
  familyRef,
  onRefreshSuggestions,
  onVideoSetupJoin,
}: Props) => {
  const isMobile = useIsMobile();
  const { videoCallState } = useContext(VideoCallContext);

  const isNoteExpanded =
    videoRoom?.state === VideoRoom_State.ACTIVE &&
    videoCallState.activeVideoRoom !== null &&
    !videoCallState.manuallyEnded;
  if (
    videoRoom &&
    !isCallActive &&
    !videoCallState.manuallyEnded &&
    videoRoom.status !== "completed"
  ) {
    return (
      <VideoCallSetup
        videoRoomUuid={videoRoom.uuid}
        familyRef={familyRef}
        onJoin={onVideoSetupJoin || (() => {})}
      />
    );
  }

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      gap={"32px"}
      marginTop={"8px"}
      paddingRight="20px"
      paddingLeft="20px"
      height="100%"
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={"12px"}
        marginBottom={"24px"}
        height="100%"
      >
        {(isCallActive || videoCallState.activeVideoRoom) &&
        videoCallState.shouldRenderLiveKit ? (
          <Box
            sx={{
              width: "100%",
              alignSelf: "center",
              position: "relative",
              height: "100%",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: "12px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <VideoCallContainer
                containerId="video-main-container"
                containerType="main"
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "400px",
              gap: 3,
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              border: "1px dashed #ccc",
              alignSelf: "center",
              width: "100%",
              padding: isMobile ? "16px" : "32px",
            }}
          >
            <Typography variant="h5" gutterBottom>
              Video Call Ended
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
              The video call has ended. Click below to rejoin.
            </Typography>
            <Button
              variant="contained"
              startIcon={<VideoIcon />}
              onClick={onReJoin}
            >
              Re-join call
            </Button>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "start",
            alignSelf: "center",
            width: "100%",
          }}
        >
          <Box sx={{ alignSelf: "center", width: "100%" }}>
            {videoRoom && suggestions && (
              <SuggestionsSection
                videoRoom={videoRoom}
                suggestions={suggestions}
                familyRef={familyRef}
                title={"Smart Suggestions"}
                onRefreshSuggestions={onRefreshSuggestions}
              />
            )}
          </Box>
          <Typography variant="h4">Notes</Typography>
          <Box sx={{ alignSelf: "center", width: "100%" }}>
            {note && (
              <NotesEditor
                note={note}
                title={"Notes"}
                initialContent={""}
                style={{ border: "none", padding: "0", boxShadow: "none" }}
                editorType="simple"
                editorMinHeight={isNoteExpanded ? "7vh" : "fit-content"}
                editorMaxHeight={isNoteExpanded ? "7vh" : undefined}
                scrollable={isNoteExpanded}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
